import { EntityType, useAppSelector } from '@frontend/common';
import { ICONS, iconByType } from '@frontend/icons';
import { useLocale } from '@frontend/lang';
import { awaitResolve, getResolvedEntity, resolveStore } from '@frontend/repository';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useSelector } from 'react-redux';

import { usePlatformModalHost } from '../platform-modal-host/platform-modal-host.context';
import { getReactiveDetailRegistryEntry } from '../reactive-detail-shell/reactive-detail-registry';
import { createPlatformObjectBadgeUtils } from './platform-object-badge.utils';

export interface PlatformObjectBadgeProps {
    id?: string;
    entityType: EntityType | string;
    display?: string;
    className?: string;
    disableClick?: boolean;
    hideIcon?: boolean;
    /**
     * Parent IDs required for nested entity fetches (e.g. slot → spot_id + module_id).
     * Stored on this pane's `platform_details` entry on open.
     */
    parentIds?: Record<string, string>;
    /** True when this nested entity belongs to an account other than the requesting user's. */
    foreign?: boolean;
    /** @deprecated Unused on the badge (list share column uses ForeignEntityMarker). Kept for existing call sites. */
    accountId?: string | null;
    /** @deprecated Unused on the badge (list share column uses ForeignEntityMarker). Kept for existing call sites. */
    accountName?: string | null;
    clearable?: {
        onClear: () => void;
    };
    overwriteDisplayMap?: (entity: any) => string;
    styles?: {
        icon?: string;
    };
}

/**
 * Platform-only entity badge. Opens stacked detail modals via PlatformModalHost.
 *
 * Label resolution uses the resolve store (no useReactiveObject / react-router),
 * so it is safe inside ModalProvider portals (e.g. transaction detail on /pudo/transactions).
 *
 * Foreign badges always resolve to check read rights: success → clickable; failure → disabled.
 * While that resolve is in flight the badge pulses (keeps any provided display name; only shows
 * the loading placeholder when no display label is available yet).
 *
 * BadgeUtils must be constructed at this component's top level — its requestHandlers
 * call useAppSelector (same pattern as useObjectBadge).
 */
export const PlatformObjectBadge = (props: PlatformObjectBadgeProps) => {
    const { locale } = useLocale();
    const { resolve, mapDisplayName } = createPlatformObjectBadgeUtils(props.id || '', props.entityType, locale, props.display, props.overwriteDisplayMap);
    const { openPlatformDetail } = usePlatformModalHost();
    const resolveState = useAppSelector(useSelector, resolveStore);
    const requestedIdRef = useRef<string | undefined>(undefined);
    const [settledId, setSettledId] = useState<string | undefined>(undefined);
    const isForeign = Boolean(props.foreign);
    /** Foreign badges always resolve for read-rights even when `display` is provided. */
    const skipResolveForLabel = props.display !== undefined && !isForeign;

    const entity = useMemo(() => {
        if (!props.id || skipResolveForLabel) {
            return null;
        }
        return getResolvedEntity<{ id: string }>(resolveState, props.entityType as EntityType, props.id);
    }, [props.id, skipResolveForLabel, props.entityType, resolveState]);

    const resolveSettled = skipResolveForLabel || !props.id || settledId === props.id || Boolean(entity);

    useEffect(() => {
        const id = props.id;
        if (skipResolveForLabel || !id || entity || requestedIdRef.current === id) {
            return;
        }
        requestedIdRef.current = id;
        void awaitResolve(id, resolve).then(() => {
            if (requestedIdRef.current === id) {
                setSettledId(id);
            }
        });
    }, [skipResolveForLabel, props.id, entity, resolve]);

    const isForeignResolving = isForeign && !!props.id && !entity && !resolveSettled;

    const displayName = useMemo(() => {
        if (props.display !== undefined) {
            return props.display;
        }
        if (isForeignResolving) {
            return undefined;
        }
        if (!props.id) {
            return (
                <FormattedMessage
                    id='badge.none'
                    defaultMessage='None'
                />
            );
        }
        if (entity) {
            return mapDisplayName(entity) ?? props.id;
        }
        if (resolveSettled) {
            return (
                <FormattedMessage
                    id='badge.not_found'
                    defaultMessage='Not found'
                />
            );
        }
        return undefined;
    }, [isForeignResolving, props.display, props.id, entity, resolveSettled, mapDisplayName]);

    const Icon = iconByType(props.entityType as EntityType);
    const isDisabled = !props.id || (isForeign && resolveSettled && !entity) || (!isForeign && props.display === undefined && !entity && resolveSettled);
    const canOpenDetail = Boolean(getReactiveDetailRegistryEntry(props.entityType));
    // Own-account with display can open without a store hit; foreign needs a successful resolve.
    const isClickable = !props.disableClick && canOpenDetail && !!props.id && !isDisabled && (!isForeign || Boolean(entity));
    const showLoadingLabel = displayName === undefined;

    const styles = {
        default:
            'ignore-modal-close relative px-1 justify-start items-center text-brand-primary text-sm font-semibold rounded-sm inline-flex items-center gap-1 hover:bg-brand-primary hover:text-background-surface hover:cursor-pointer',
        disabled:
            'ignore-modal-close relative px-1 justify-start items-center text-brand-muted text-sm font-semibold rounded-sm inline-flex items-center gap-1 cursor-default'
    };

    const baseBadgeClassName = props.className ? `ignore-modal-close relative ${props.className}` : isClickable ? styles.default : styles.disabled;
    const badgeClassName = isForeignResolving ? `${baseBadgeClassName} animate-pulse` : baseBadgeClassName;

    return (
        <span
            onMouseDown={isClickable ? (e) => e.stopPropagation() : undefined}
            onClick={
                isClickable
                    ? (e) => {
                          e.stopPropagation();
                          if (!props.id) {
                              return;
                          }
                          openPlatformDetail({
                              entityType: props.entityType,
                              id: props.id,
                              parents: props.parentIds
                          });
                      }
                    : undefined
            }
            id={'platform-badge-' + props.id}
            data-testid={props.id ? `platform-badge-${props.id}` : 'platform-badge'}
            className={badgeClassName}>
            {!props.hideIcon &&
                (Icon ? (
                    <Icon className={props.styles?.icon ? props.styles.icon : `size-4 ${isDisabled ? 'text-primary/50' : ''}`} />
                ) : (
                    <span className='placeholder-glow'>
                        <span className='placeholder'>loading</span>
                    </span>
                ))}

            {showLoadingLabel ? (
                <span
                    data-testid='badge-loading-glow'
                    className='placeholder-glow'>
                    <span className='placeholder'>loading</span>
                </span>
            ) : (
                <span className='inline-flex min-w-0 items-center'>{displayName}</span>
            )}
            {props.clearable && (
                <span
                    className='cursor-pointer'
                    onClick={(e) => {
                        e.stopPropagation();
                        props.clearable?.onClear();
                    }}>
                    <ICONS.BUTTON.CLOSE_2 className='text-background-surface size-4' />
                </span>
            )}
        </span>
    );
};
